import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    IconButton,
    Button,
    Stack,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Switch,
    FormControlLabel,
    Tooltip,
    CircularProgress,
    Alert,
    Avatar,
    Badge,
    InputAdornment,
    Grid,
    Fade,
    Zoom
} from '@mui/material';
import {
    Edit,
    Delete,
    Add,
    Restore,
    Category,
    Palette,
    Search,
    FilterList,
    Refresh,
    Circle
} from '@mui/icons-material';

import {
    getCategories,
    createCategory,
    updateCategory,
    deleteCategory,
    reactivateCategory
} from '../../../api/admin';

const CategoryManagementTable = () => {
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [searchTerm, setSearchTerm] = useState('');

    // Dialog states
    const [createDialog, setCreateDialog] = useState({
        open: false,
        name: '',
        description: '',
        color: '#4caf50',
        suggestion: '',
        isActive: true
    });
    const [editDialog, setEditDialog] = useState({
        open: false,
        category: null,
        name: '',
        description: '',
        color: '#4caf50',
        suggestion: '',
        isActive: true
    });
    const [deleteDialog, setDeleteDialog] = useState({ open: false, category: null });

    const fetchCategories = async () => {
        try {
            setLoading(true);
            setError('');
            const response = await getCategories();
            setCategories(response.data || []);
        } catch (error) {
            console.error('Failed to fetch categories:', error);
            setError('Failed to load categories. Please check your connection.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCategories();
    }, []);

    const handleCreateCategory = async () => {
        try {
            const categoryData = {
                name: createDialog.name,
                description: createDialog.description,
                color: createDialog.color,
                suggestion: createDialog.suggestion,
                isActive: createDialog.isActive
            };
            await createCategory(categoryData);
            setSuccess('Category created successfully!');
            setCreateDialog({
                open: false,
                name: '',
                description: '',
                color: '#4caf50',
                suggestion: '',
                isActive: true
            });
            fetchCategories();
        } catch (error) {
            console.error('Failed to create category:', error);
            setError('Failed to create category. Please try again.');
        }
    };

    const handleUpdateCategory = async () => {
        try {
            const categoryData = {
                name: editDialog.name,
                description: editDialog.description,
                color: editDialog.color,
                suggestion: editDialog.suggestion,
                isActive: editDialog.isActive
            };
            await updateCategory(editDialog.category.id, categoryData);
            setSuccess('Category updated successfully!');
            setEditDialog({
                open: false,
                category: null,
                name: '',
                description: '',
                color: '#4caf50',
                suggestion: '',
                isActive: true
            });
            fetchCategories();
        } catch (error) {
            console.error('Failed to update category:', error);
            setError('Failed to update category. Please try again.');
        }
    };

    const handleDeleteCategory = async () => {
        try {
            await deleteCategory(deleteDialog.category.id);
            setSuccess('Category deactivated successfully!');
            setDeleteDialog({ open: false, category: null });
            fetchCategories();
        } catch (error) {
            console.error('Failed to delete category:', error);
            setError('Failed to deactivate category. Please try again.');
        }
    };

    const handleReactivateCategory = async (categoryId) => {
        try {
            await reactivateCategory(categoryId);
            setSuccess('Category reactivated successfully!');
            fetchCategories();
        } catch (error) {
            console.error('Failed to reactivate category:', error);
            setError('Failed to reactivate category. Please try again.');
        }
    };

    const openEditDialog = (category) => {
        setEditDialog({
            open: true,
            category,
            name: category.name,
            description: category.description,
            color: category.color || '#4caf50',
            suggestion: category.suggestion || '',
            isActive: category.isActive
        });
    };

    const predefinedColors = [
        { color: '#4caf50', name: 'Green' },
        { color: '#2196f3', name: 'Blue' },
        { color: '#f44336', name: 'Red' },
        { color: '#ff9800', name: 'Orange' },
        { color: '#9c27b0', name: 'Purple' },
        { color: '#607d8b', name: 'Blue Grey' },
        { color: '#795548', name: 'Brown' },
        { color: '#e91e63', name: 'Pink' },
        { color: '#ffc107', name: 'Amber' },
        { color: '#009688', name: 'Teal' },
        { color: '#cddc39', name: 'Lime' },
        { color: '#3f51b5', name: 'Indigo' }
    ];

    const filteredCategories = categories.filter(category =>
        category.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        category.description?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const formatDate = (dateString) => {
        if (!dateString) return 'Unknown';
        try {
            return new Date(dateString).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            });
        } catch (error) {
            return 'Invalid Date';
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                <Fade in={loading} timeout={500}>
                    <Stack alignItems="center" spacing={3}>
                        <Zoom in={true} timeout={500}>
                            <Avatar sx={{
                                width: 80,
                                height: 80,
                                background: 'linear-gradient(135deg, #ff9800, #ffb74d)',
                                animation: 'pulse 2s infinite',
                                '@keyframes pulse': {
                                    '0%': { transform: 'scale(1)' },
                                    '50%': { transform: 'scale(1.1)' },
                                    '100%': { transform: 'scale(1)' }
                                }
                            }}>
                                <Category sx={{ fontSize: 40, color: 'white' }} />
                            </Avatar>
                        </Zoom>
                        <Typography variant="h6" fontWeight={600} color="text.secondary">
                            Loading Categories...
                        </Typography>
                    </Stack>
                </Fade>
            </Box>
        );
    }

    return (
        <Box>
            {/* Alerts */}
            {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
                    {error}
                </Alert>
            )}
            {success && (
                <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
                    {success}
                </Alert>
            )}

            <Card elevation={6} sx={{ borderRadius: 4, overflow: 'hidden' }}>
                {/* Enhanced Header */}
                <Box sx={{
                    p: 4,
                    background: 'linear-gradient(135deg, #ff9800, #ffb74d)',
                    color: 'white',
                    position: 'relative',
                    overflow: 'hidden'
                }}>
                    <Box sx={{
                        position: 'absolute',
                        top: -50,
                        right: -50,
                        width: 200,
                        height: 200,
                        borderRadius: '50%',
                        background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
                        animation: 'float 6s ease-in-out infinite',
                        '@keyframes float': {
                            '0%': { transform: 'translateY(0px)' },
                            '50%': { transform: 'translateY(-20px)' },
                            '100%': { transform: 'translateY(0px)' }
                        }
                    }} />

                    <Grid container spacing={4} alignItems="center">
                        <Grid item xs={12} md={8}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 2 }}>
                                <Avatar sx={{
                                    background: 'rgba(255,255,255,0.2)',
                                    backdropFilter: 'blur(10px)',
                                    border: '3px solid rgba(255,255,255,0.3)',
                                    width: 70,
                                    height: 70
                                }}>
                                    <Category sx={{ fontSize: 35 }} />
                                </Avatar>
                                <Box>
                                    <Typography variant="h4" fontWeight={800} sx={{ mb: 1 }}>
                                        🗂️ Advanced Category Management
                                    </Typography>
                                    <Typography variant="h6" sx={{ opacity: 0.9 }}>
                                        Manage waste classification categories with ease
                                    </Typography>
                                </Box>
                            </Box>

                            <Stack direction="row" spacing={2} flexWrap="wrap">
                                <Chip
                                    icon={<Category />}
                                    label={`${categories.length} Total Categories`}
                                    sx={{ background: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 600 }}
                                />
                                <Chip
                                    label={`${categories.filter(c => c.isActive).length} Active`}
                                    sx={{ background: 'rgba(76, 175, 80, 0.8)', color: 'white', fontWeight: 600 }}
                                />
                                <Chip
                                    label={`${categories.filter(c => !c.isActive).length} Inactive`}
                                    sx={{ background: 'rgba(244, 67, 54, 0.8)', color: 'white', fontWeight: 600 }}
                                />
                            </Stack>
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <Button
                                variant="contained"
                                startIcon={<Add />}
                                onClick={() => setCreateDialog({
                                    open: true,
                                    name: '',
                                    description: '',
                                    color: '#4caf50',
                                    suggestion: '',
                                    isActive: true
                                })}
                                sx={{
                                    background: 'rgba(255,255,255,0.2)',
                                    '&:hover': { background: 'rgba(255,255,255,0.3)' },
                                    fontWeight: 600,
                                    fontSize: '1.1rem',
                                    py: 1.5
                                }}
                                fullWidth
                                size="large"
                            >
                                Create New Category
                            </Button>
                        </Grid>
                    </Grid>
                </Box>

                <CardContent sx={{ p: 0 }}>
                    {/* Search and Filter Section */}
                    <Box sx={{ p: 3, borderBottom: '1px solid #e0e0e0', backgroundColor: '#f8f9fa' }}>
                        <Grid container spacing={3} alignItems="center">
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    placeholder="Search categories by name or description..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Search color="action" />
                                            </InputAdornment>
                                        ),
                                    }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            backgroundColor: 'white',
                                            borderRadius: 2
                                        }
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Button
                                    variant="outlined"
                                    startIcon={<Refresh />}
                                    onClick={fetchCategories}
                                    sx={{ borderRadius: 2 }}
                                >
                                    Refresh Categories
                                </Button>
                            </Grid>
                        </Grid>
                    </Box>

                    {/* Enhanced Table */}
                    <TableContainer>
                        <Table sx={{ minWidth: 800 }}>
                            <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 700, fontSize: '0.9rem' }}>Category</TableCell>
                                    <TableCell sx={{ fontWeight: 700, fontSize: '0.9rem' }}>Description</TableCell>
                                    <TableCell sx={{ fontWeight: 700, fontSize: '0.9rem' }}>Suggestion</TableCell>
                                    <TableCell sx={{ fontWeight: 700, fontSize: '0.9rem' }}>Status</TableCell>
                                    <TableCell sx={{ fontWeight: 700, fontSize: '0.9rem' }}>Created</TableCell>
                                    <TableCell sx={{ fontWeight: 700, fontSize: '0.9rem' }}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredCategories.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={6} sx={{ textAlign: 'center', py: 8 }}>
                                            <Category sx={{ fontSize: 60, color: '#ccc', mb: 2 }} />
                                            <Typography variant="h6" color="text.secondary">
                                                No categories found
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {searchTerm ? 'Try adjusting your search terms' : 'Start by creating your first category'}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    filteredCategories.map((category) => (
                                        <TableRow key={category.id} hover sx={{ height: 80 }}>
                                            <TableCell>
                                                <Stack direction="row" spacing={2} alignItems="center">
                                                    <Badge
                                                        overlap="circular"
                                                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                                        badgeContent={
                                                            <Circle sx={{
                                                                fontSize: 12,
                                                                color: category.isActive ? '#4caf50' : '#f44336'
                                                            }} />
                                                        }
                                                    >
                                                        <Avatar
                                                            sx={{
                                                                width: 45,
                                                                height: 45,
                                                                backgroundColor: category.color || '#4caf50',
                                                                border: '3px solid #fff',
                                                                boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                                                            }}
                                                        >
                                                            <Category sx={{ color: 'white' }} />
                                                        </Avatar>
                                                    </Badge>
                                                    <Box>
                                                        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>
                                                            {category.name}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            ID: #{category.id}
                                                        </Typography>
                                                    </Box>
                                                </Stack>
                                            </TableCell>
                                            <TableCell>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        maxWidth: 250,
                                                        display: '-webkit-box',
                                                        WebkitLineClamp: 2,
                                                        WebkitBoxOrient: 'vertical',
                                                        overflow: 'hidden'
                                                    }}
                                                >
                                                    {category.description}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography
                                                    variant="body2"
                                                    color={category.suggestion ? 'text.primary' : 'text.secondary'}
                                                    sx={{
                                                        maxWidth: 200,
                                                        display: '-webkit-box',
                                                        WebkitLineClamp: 2,
                                                        WebkitBoxOrient: 'vertical',
                                                        overflow: 'hidden'
                                                    }}
                                                >
                                                    {category.suggestion || 'No suggestion provided'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={category.isActive ? 'Active' : 'Inactive'}
                                                    size="small"
                                                    color={category.isActive ? 'success' : 'error'}
                                                    variant="outlined"
                                                    sx={{ fontWeight: 600 }}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" fontWeight={500}>
                                                    {formatDate(category.createdAt)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Stack direction="row" spacing={0.5}>
                                                    <Tooltip title="Edit Category">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => openEditDialog(category)}
                                                            sx={{
                                                                color: '#2196f3',
                                                                '&:hover': { backgroundColor: '#2196f320' }
                                                            }}
                                                        >
                                                            <Edit fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>

                                                    {!category.isActive ? (
                                                        <Tooltip title="Reactivate Category">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleReactivateCategory(category.id)}
                                                                sx={{
                                                                    color: '#4caf50',
                                                                    '&:hover': { backgroundColor: '#4caf5020' }
                                                                }}
                                                            >
                                                                <Restore fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    ) : (
                                                        <Tooltip title="Deactivate Category">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => setDeleteDialog({ open: true, category })}
                                                                sx={{
                                                                    color: '#f44336',
                                                                    '&:hover': { backgroundColor: '#f4433620' }
                                                                }}
                                                            >
                                                                <Delete fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </CardContent>
            </Card>

            {/* Create Category Dialog */}
            <Dialog
                open={createDialog.open}
                onClose={() => setCreateDialog({
                    open: false,
                    name: '',
                    description: '',
                    color: '#4caf50',
                    suggestion: '',
                    isActive: true
                })}
                maxWidth="md"
                fullWidth
                PaperProps={{ sx: { borderRadius: 3 } }}
            >
                <DialogTitle sx={{
                    background: 'linear-gradient(135deg, #43a047, #66bb6a)',
                    color: 'white',
                    textAlign: 'center'
                }}>
                    <Typography variant="h5" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                        <Category />
                        Create New Category
                    </Typography>
                </DialogTitle>
                <DialogContent sx={{ p: 4 }}>
                    <Stack spacing={3} sx={{ mt: 1 }}>
                        <TextField
                            fullWidth
                            label="Category Name"
                            value={createDialog.name}
                            onChange={(e) => setCreateDialog({ ...createDialog, name: e.target.value })}
                            required
                            placeholder="e.g., Plastic Bottles"
                        />
                        <TextField
                            fullWidth
                            label="Description"
                            multiline
                            rows={3}
                            value={createDialog.description}
                            onChange={(e) => setCreateDialog({ ...createDialog, description: e.target.value })}
                            placeholder="Detailed description of what items belong to this category..."
                        />
                        <TextField
                            fullWidth
                            label="Disposal Suggestion"
                            multiline
                            rows={2}
                            value={createDialog.suggestion}
                            onChange={(e) => setCreateDialog({ ...createDialog, suggestion: e.target.value })}
                            placeholder="Instructions for proper disposal of items in this category..."
                        />
                        <Box>
                            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                                🎨 Category Color
                            </Typography>
                            <Grid container spacing={1}>
                                {predefinedColors.map((colorObj) => (
                                    <Grid item key={colorObj.color}>
                                        <Tooltip title={colorObj.name}>
                                            <Box
                                                sx={{
                                                    width: 40,
                                                    height: 40,
                                                    borderRadius: 2,
                                                    backgroundColor: colorObj.color,
                                                    border: createDialog.color === colorObj.color ?
                                                        '3px solid #000' : '2px solid #e0e0e0',
                                                    cursor: 'pointer',
                                                    boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                                                    transition: 'all 0.2s ease',
                                                    '&:hover': {
                                                        transform: 'scale(1.1)',
                                                        boxShadow: '0 4px 16px rgba(0,0,0,0.3)'
                                                    }
                                                }}
                                                onClick={() => setCreateDialog({ ...createDialog, color: colorObj.color })}
                                            />
                                        </Tooltip>
                                    </Grid>
                                ))}
                            </Grid>
                        </Box>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={createDialog.isActive}
                                    onChange={(e) => setCreateDialog({ ...createDialog, isActive: e.target.checked })}
                                />
                            }
                            label="Active Category"
                        />
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button
                        onClick={() => setCreateDialog({
                            open: false,
                            name: '',
                            description: '',
                            color: '#4caf50',
                            suggestion: '',
                            isActive: true
                        })}
                        variant="outlined"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleCreateCategory}
                        variant="contained"
                        disabled={!createDialog.name.trim()}
                        sx={{
                            background: 'linear-gradient(135deg, #43a047, #66bb6a)',
                            '&:hover': {
                                background: 'linear-gradient(135deg, #2e7d32, #43a047)'
                            }
                        }}
                    >
                        Create Category
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Edit Category Dialog */}
            <Dialog
                open={editDialog.open}
                onClose={() => setEditDialog({
                    open: false,
                    category: null,
                    name: '',
                    description: '',
                    color: '#4caf50',
                    suggestion: '',
                    isActive: true
                })}
                maxWidth="md"
                fullWidth
                PaperProps={{ sx: { borderRadius: 3 } }}
            >
                <DialogTitle sx={{
                    background: 'linear-gradient(135deg, #2196f3, #42a5f5)',
                    color: 'white',
                    textAlign: 'center'
                }}>
                    <Typography variant="h5" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                        <Edit />
                        Edit Category
                    </Typography>
                </DialogTitle>
                <DialogContent sx={{ p: 4 }}>
                    <Stack spacing={3} sx={{ mt: 1 }}>
                        <TextField
                            fullWidth
                            label="Category Name"
                            value={editDialog.name}
                            onChange={(e) => setEditDialog({ ...editDialog, name: e.target.value })}
                            required
                        />
                        <TextField
                            fullWidth
                            label="Description"
                            multiline
                            rows={3}
                            value={editDialog.description}
                            onChange={(e) => setEditDialog({ ...editDialog, description: e.target.value })}
                        />
                        <TextField
                            fullWidth
                            label="Disposal Suggestion"
                            multiline
                            rows={2}
                            value={editDialog.suggestion}
                            onChange={(e) => setEditDialog({ ...editDialog, suggestion: e.target.value })}
                        />
                        <Box>
                            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                                🎨 Category Color
                            </Typography>
                            <Grid container spacing={1}>
                                {predefinedColors.map((colorObj) => (
                                    <Grid item key={colorObj.color}>
                                        <Tooltip title={colorObj.name}>
                                            <Box
                                                sx={{
                                                    width: 40,
                                                    height: 40,
                                                    borderRadius: 2,
                                                    backgroundColor: colorObj.color,
                                                    border: editDialog.color === colorObj.color ?
                                                        '3px solid #000' : '2px solid #e0e0e0',
                                                    cursor: 'pointer',
                                                    boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                                                    transition: 'all 0.2s ease',
                                                    '&:hover': {
                                                        transform: 'scale(1.1)',
                                                        boxShadow: '0 4px 16px rgba(0,0,0,0.3)'
                                                    }
                                                }}
                                                onClick={() => setEditDialog({ ...editDialog, color: colorObj.color })}
                                            />
                                        </Tooltip>
                                    </Grid>
                                ))}
                            </Grid>
                        </Box>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={editDialog.isActive}
                                    onChange={(e) => setEditDialog({ ...editDialog, isActive: e.target.checked })}
                                />
                            }
                            label="Active Category"
                        />
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button
                        onClick={() => setEditDialog({
                            open: false,
                            category: null,
                            name: '',
                            description: '',
                            color: '#4caf50',
                            suggestion: '',
                            isActive: true
                        })}
                        variant="outlined"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleUpdateCategory}
                        variant="contained"
                        disabled={!editDialog.name.trim()}
                        sx={{
                            background: 'linear-gradient(135deg, #2196f3, #42a5f5)',
                            '&:hover': {
                                background: 'linear-gradient(135deg, #1976d2, #2196f3)'
                            }
                        }}
                    >
                        Update Category
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Category Dialog */}
            <Dialog
                open={deleteDialog.open}
                onClose={() => setDeleteDialog({ open: false, category: null })}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: 3 } }}
            >
                <DialogTitle sx={{ color: '#f44336', textAlign: 'center' }}>
                    <Typography variant="h5" fontWeight={700}>
                        ⚠️ Deactivate Category
                    </Typography>
                </DialogTitle>
                <DialogContent sx={{ p: 3 }}>
                    <Typography variant="body1" sx={{ textAlign: 'center' }}>
                        Are you sure you want to deactivate category
                        <strong> "{deleteDialog.category?.name}"</strong>?
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
                        This will soft-delete the category and it can be reactivated later.
                        Existing classifications will not be affected.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ p: 3, justifyContent: 'center' }}>
                    <Button
                        onClick={() => setDeleteDialog({ open: false, category: null })}
                        variant="outlined"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleDeleteCategory}
                        color="error"
                        variant="contained"
                        startIcon={<Delete />}
                    >
                        Deactivate Category
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default CategoryManagementTable;
